import { Component, OnInit, ChangeDetectorRef, ViewChild, HostListener, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Header } from './header/header';
import { Sidebar } from './sidebar/sidebar';
import { Footer } from './footer/footer';
import { LayoutService } from '../core/services/layout/layout.service';
import { NavigationItem } from '../core/services/sidebar/sidebar.service';
import { RightSidebarComponent } from './right-sidebar/right-sidebar';
import { ChangePasswordFormComponent } from '../shared/components/change-password-form/change-password-form';
import { ThemeSelectionModalComponent } from '../shared/components/theme-selection-modal/theme-selection-modal.component';
import { MySettings, UserSettings } from '../shared/components/my-settings/my-settings';
import { NotificationService, Notification } from '../core/services/notifications/notification.service';
import { MetaService } from '../core/services/meta/meta.service';
import { filter, take } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.html',
  styleUrls: ['./layout.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    Header,
    Sidebar,
    Footer,
    RightSidebarComponent,
    ChangePasswordFormComponent,
    ThemeSelectionModalComponent,
    MySettings,
    MatIconModule,
  ]
})
export class Layout implements OnInit {
  @ViewChild(Sidebar) sidebarComponent!: Sidebar;
  @ViewChild('headerComp') headerComponent!: Header;

  activeHeader: NavigationItem | null = null;
  isChangePasswordOpen = false;
  themeSelectionModalOpen = false;
  showSettingsSlider = false;
  profileDropdownOpen = false;
  profileDropdownUserName: string | null = null;
  profileDropdownUserImage: string | null = null;
  private _isOnProjectDetailsPage: boolean = false;
  profileDropdownUserId: string | null = null;
  
  // Mobile radial menu properties
  isMobileRadialMenuOpen = false;
  mobileRadialMenuItems = [
    {
      id: 'timesheets',
      label: 'Timesheets',
      icon: 'assets/images/timesheet.png',
      route: '/timesheets'
    },
    {
      id: 'projects-all',
      label: 'Projects',
      icon: 'assets/images/all-projects.png',
      route: '/projects/projects-all'
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'assets/images/dashboard.png',
      route: '/dashboard'
    },
    {
      id: 'requests',
      label: 'Requests',
      icon: 'assets/images/request.png',
      route: '/requests'
    },
    {
      id: 'clients',
      label: 'Clients',
      icon: 'assets/images/clients.png',
      route: '/clients-list'
    }
  ];
  // showClientSummarySlider = false;

  userInfoBg: string = '';
  private banners: string[] = [
    'assets/images/theme-banner-dark.png',
    'assets/images/theme-banner-ocean.png',
    'assets/images/theme-banner.png'
  ];
  private bannerIndex: number = 2;

  // Store original state for preview functionality
  private originalBackgroundImage: string = '';
  private originalUserInfoBg: string = '';
  private originalThemeState: boolean = false;

  // Add properties to track cleanup
  private backgroundUpdateInterval: any;
  private mutationObserver: MutationObserver | null = null;
  private storageEventListener: ((event: StorageEvent) => void) | null = null;
  private lastLoggedTheme: string = '';
  private mutationTimeout: any = null;
  private routeChangeTimeout: any = null;
  
  // Add flag to prevent infinite loops in theme consistency checking
  private isThemeConsistencyCheckInProgress: boolean = false;
  private lastThemeClearLog: number = 0;
  private lastLoggedBackground: string = '';
  /** Used so we only clear notification popups when leaving the dashboard, not on every in-app navigation. */
  private previousUrlAfterRedirects = '';

  // Notification service signals - use computed for proper reactivity
  // Only show popup notifications on dashboard page
  popupNotifications = computed(() => {
    // const notifications = this.notificationService.popupNotifications();
    // // Filter to only show on dashboard
    // const isOnDashboard = /^\/dashboard/.test(this.router.url);
    // return isOnDashboard ? notifications : [];
    return this.notificationService.popupNotifications();
  });
  
  // Track image loading errors
  imageLoadError: { [key: string]: boolean } = {};

  // Popup dropdown state
  popupDropdownOpen = signal<{ [key: string]: boolean }>({});

  constructor(
    public layoutService: LayoutService,
    public router: Router,
    private cdr: ChangeDetectorRef,
    public notificationService: NotificationService,
    private metaService: MetaService
  ) {
    // No need to initialize here since we're using computed
  }

  ngOnInit() {
    // Initialize user info background with time-based image
    this.userInfoBg = this.getTimeBasedBackground();

    this.layoutService.getActiveHeader().subscribe(header => {
      this.activeHeader = header;
    });

    // Listen to route changes to detect project-details page and handle theme application
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.checkIfOnProjectDetailsPage();

      const url = event.urlAfterRedirects;
      const isOnDashboard = /^\/dashboard/.test(url);
      const wasOnDashboard = /^\/dashboard/.test(this.previousUrlAfterRedirects);
      this.previousUrlAfterRedirects = url;

      // Only dismiss popups when actually leaving the dashboard. Clearing on every non-dashboard
      // NavigationEnd removed task-assignment toasts while navigating tasks/requests/revisions.
      if (wasOnDashboard && !isOnDashboard) {
        const currentPopups = this.notificationService.popupNotifications();
        currentPopups.forEach(popup => {
          this.notificationService.dismissPopup(popup.id);
        });

        this.layoutService.rightSidebarContent$.pipe(take(1)).subscribe(content => {
          if (content === 'notifications') {
            this.layoutService.setRightSidebarContent(null);
          }
        });
      }

      // Ensure theme is applied consistently after navigation (with throttling)
      // (excluding login page which is handled separately)
      if (this.routeChangeTimeout) {
        clearTimeout(this.routeChangeTimeout);
      }
      this.routeChangeTimeout = setTimeout(() => {
        this.ensureThemeConsistency();
      }, 200);
    });

    // Check initial route
    this.checkIfOnProjectDetailsPage();

    // Load saved background theme on init - this ensures theme is restored after login
    this.loadSavedBackgroundTheme();

    // Set up periodic background updates (every 30 minutes)
    this.setupPeriodicBackgroundUpdate();

    // Set up theme consistency monitoring
    this.setupThemeConsistencyMonitoring();
  }

  private checkIfOnProjectDetailsPage(): void {
    const currentUrl = this.router.url;
    // Check if URL matches pattern: projects/project-details/{id}
    this._isOnProjectDetailsPage = /^\/projects\/project-details\/[^\/]+$/.test(currentUrl);
  }

  isOnProjectDetailsPage(): boolean {
    return this._isOnProjectDetailsPage;
  }

  // onClientSummaryClick(): void {
  //   this.showClientSummarySlider = true;
  // }

  // onCloseClientSummarySlider(): void {
  //   this.showClientSummarySlider = false;
  // }

  toggleSidebar() {
    this.layoutService.toggleSidebar();
  }

  cycleUserInfoBg() {
    this.bannerIndex = (this.bannerIndex + 1) % this.banners.length;
    this.userInfoBg = this.banners[this.bannerIndex];
  }

  onChangePasswordPanelToggled(event: any) {
    this.isChangePasswordOpen = event;
    // Close profile dropdown when opening change password form
    if (event) {
      this.profileDropdownOpen = false;
    }
  }

  onBackgroundImageSelected(imagePath: string) {
    if (imagePath === '') {
      // Clear theme and restore original backgrounds consistently
      this.clearThemeConsistently();
      // Also clear from localStorage
      localStorage.removeItem('selectedBackgroundTheme');
      localStorage.removeItem('theme-background-active');
    } else {
      // Apply theme consistently to both body and user_info
      this.applyThemeConsistently(imagePath);
      // Save to localStorage
      localStorage.setItem('selectedBackgroundTheme', imagePath);
      localStorage.setItem('theme-background-active', 'true');
    }

    // Clear original state as changes are now permanent
    this.resetOriginalState();
  }

  onBackgroundImagePreview(imagePath: string) {
    // Store original state before first preview
    if (!this.originalBackgroundImage) {
      this.originalBackgroundImage = document.body.style.backgroundImage;
      this.originalUserInfoBg = this.userInfoBg;
      this.originalThemeState = this.isThemeActive();
    }

    if (imagePath === '') {
      // Preview clear theme - show time-based background consistently
      this.clearThemeConsistently();
    } else {
      // Preview theme - apply temporarily but consistently
      this.applyThemeConsistently(imagePath);
    }

    // Ensure change detection is triggered
    this.cdr.detectChanges();
  }

  private applyBackgroundToLayout(imagePath: string) {
    // Apply background image to the body with overlay
    const body = document.body;
    body.style.backgroundImage = `url(${imagePath})`;
    body.style.backgroundSize = 'cover';
    body.style.backgroundPosition = 'center';
    body.style.backgroundRepeat = 'no-repeat';
    body.style.backgroundAttachment = 'fixed';

    // Add overlay class to body
    body.classList.add('theme-background-active');

    // Save to localStorage
    localStorage.setItem('selectedBackgroundTheme', imagePath);
    localStorage.setItem('theme-background-active', 'true');
  }

  private applyBackgroundToUserInfo(imagePath: string) {
    // Save the current time-based background as the original
    if (!localStorage.getItem('originalUserInfoBg')) {
      localStorage.setItem('originalUserInfoBg', this.getTimeBasedBackground());
    }

    // Update user-info background with the selected theme
    this.userInfoBg = imagePath;

    // Trigger change detection to update the view immediately
    this.cdr.detectChanges();
  }

  private loadSavedBackgroundTheme() {
    const savedTheme = localStorage.getItem('selectedBackgroundTheme');
    const wasThemeActive = localStorage.getItem('theme-background-active') === 'true';

    if (savedTheme && wasThemeActive) {
      // Use the new consistent theme application method
      this.applyThemeConsistently(savedTheme);
    } else {
      // Use the new consistent theme clearing method
      this.clearThemeConsistently();
    }
  }

  clearBackgroundTheme() {
    this.clearThemeConsistently();
    // Also clear from localStorage
    localStorage.removeItem('selectedBackgroundTheme');
    localStorage.removeItem('theme-background-active');
  }

  private restoreOriginalUserInfoBackground() {
    // Always use the current time-based background when theme is disabled
    this.userInfoBg = this.getTimeBasedBackground();
    localStorage.removeItem('originalUserInfoBg');

    // Trigger change detection to update the view immediately
    this.cdr.detectChanges();

    // Force sidebar to refresh its background with time-based image
    setTimeout(() => {
      this.refreshSidebarBackground();

      // Additional refresh to ensure the sidebar gets the updated background
      if (this.sidebarComponent) {
        this.sidebarComponent.forceRefreshTimeBasedBackground();
      }
    }, 0);

    // Final delayed update to ensure visual changes are applied
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 150);
  }

  private getTimeBasedBackground(): string {
    const currentHour = new Date().getHours();
    let background = '';
    let timePeriod = '';

    // Define time periods and their corresponding backgrounds
    if (currentHour >= 5 && currentHour < 12) {
      // Morning (5 AM - 12 PM) - Light theme banner
      background = 'assets/images/theme-banner.png';
      timePeriod = 'morning';
    } else if (currentHour >= 12 && currentHour < 17) {
      // Afternoon (12 PM - 5 PM) - Ocean theme banner
      background = 'assets/images/theme-banner-ocean.png';
      timePeriod = 'afternoon';
    } else if (currentHour >= 17 && currentHour < 21) {
      // Evening (5 PM - 9 PM) - Dark theme banner
      background = 'assets/images/theme-banner-dark.png';
      timePeriod = 'evening';
    } else {
      // Night (9 PM - 5 AM) - Dark theme banner
      background = 'assets/images/theme-banner-dark.png';
      timePeriod = 'night';
    }

    return background;
  }

  private setupPeriodicBackgroundUpdate() {
    // Update background every 30 minutes if no theme is active
    this.backgroundUpdateInterval = setInterval(() => {
      if (!this.isThemeActive()) {
        const newTimeBasedBg = this.getTimeBasedBackground();
        if (this.userInfoBg !== newTimeBasedBg) {
          this.userInfoBg = newTimeBasedBg;
          // Trigger change detection to update the view immediately
          this.cdr.detectChanges();
        }
      }
    }, 30 * 60 * 1000); // 30 minutes in milliseconds
  }

  private isThemeActive(): boolean {
    return localStorage.getItem('theme-background-active') === 'true';
  }

  public refreshUserInfoBackground() {
    // Force refresh the user info background to current time-based image
    if (!this.isThemeActive()) {
      this.userInfoBg = this.getTimeBasedBackground();
      this.cdr.detectChanges();
    }
  }

  private refreshSidebarBackground() {
    // Force sidebar to use current time-based background when theme is disabled
    if (!this.isThemeActive()) {
      this.userInfoBg = this.getTimeBasedBackground();
      this.cdr.detectChanges();

      // If sidebar component is available, force refresh its background directly
      if (this.sidebarComponent) {
        this.sidebarComponent.forceRefreshTimeBasedBackground();
      }

      // Additional force refresh after a short delay to ensure proper rendering
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 50);
    }
  }

  private previewApplyTheme(imagePath: string) {
    // Apply theme preview to body background
    const body = document.body;
    body.style.backgroundImage = `url(${imagePath})`;
    body.style.backgroundSize = 'cover';
    body.style.backgroundPosition = 'center';
    body.style.backgroundRepeat = 'no-repeat';
    body.style.backgroundAttachment = 'fixed';

    // Add theme class for overlay effects
    body.classList.add('theme-background-active');

    // Update user info background with preview
    this.userInfoBg = imagePath;

    // Force sidebar to update with preview
    if (this.sidebarComponent) {
      this.sidebarComponent.userInfoBg = imagePath;
      this.sidebarComponent.refreshUserInfoBackground();
    }
  }

  private previewClearTheme() {
    // Clear preview theme from body
    const body = document.body;
    body.style.backgroundImage = '';
    body.classList.remove('theme-background-active');

    // Restore time-based background for preview
    this.userInfoBg = this.getTimeBasedBackground();

    // Force sidebar to update with time-based background
    if (this.sidebarComponent) {
      this.sidebarComponent.forceRefreshTimeBasedBackground();
    }
  }

  private resetOriginalState() {
    // Clear stored original state after permanent changes
    this.originalBackgroundImage = '';
    this.originalUserInfoBg = '';
    this.originalThemeState = false;
  }

  showRightSidebar(): boolean {
    return this.layoutService.showRightSidebar();
  }

  private ensureThemeConsistency() {
    // Prevent infinite loops by checking if already in progress
    if (this.isThemeConsistencyCheckInProgress) {
      return;
    }

    // Skip theme application if on login page (though layout shouldn't be used for login)
    const currentRoute = this.router.url;
    if (currentRoute.includes('/login') || currentRoute.includes('/forgotpassword')) {
      return;
    }

    this.isThemeConsistencyCheckInProgress = true;

    try {
      // Check if theme should be active
      const savedTheme = localStorage.getItem('selectedBackgroundTheme');
      const isThemeActive = localStorage.getItem('theme-background-active') === 'true';

      if (savedTheme && isThemeActive) {
        // Ensure both body and user_info have the theme applied
        this.applyThemeConsistently(savedTheme);
      } else {
        // Ensure no theme is applied and time-based backgrounds are used
        this.clearThemeConsistently();
      }
    } finally {
      // Always reset the flag, even if an error occurs
      this.isThemeConsistencyCheckInProgress = false;
    }
  }

  private setupThemeConsistencyMonitoring() {
    // Monitor for changes in theme-background-active class
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const hasThemeClass = document.body.classList.contains('theme-background-active');
          const themeActiveInStorage = localStorage.getItem('theme-background-active') === 'true';

          // If there's a mismatch, synchronize (with throttling)
          if (hasThemeClass !== themeActiveInStorage) {
            // Clear existing timeout
            if (this.mutationTimeout) {
              clearTimeout(this.mutationTimeout);
            }
            
            // Throttle the consistency check to prevent excessive calls
            this.mutationTimeout = setTimeout(() => {
              this.ensureThemeConsistency();
            }, 100);
          }
        }
      });
    });

    // Start observing body class changes
    this.mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Also monitor localStorage changes (for cross-tab synchronization)
    this.storageEventListener = (event) => {
      if (event.key === 'theme-background-active' || event.key === 'selectedBackgroundTheme') {
        // Add small delay to prevent immediate re-triggering
        setTimeout(() => {
          this.ensureThemeConsistency();
        }, 50);
      }
    };
    window.addEventListener('storage', this.storageEventListener);
  }

  private applyThemeConsistently(imagePath: string) {
    // Apply to body background
    const body = document.body;
    body.style.backgroundImage = `url(${imagePath})`;
    body.style.backgroundSize = 'cover';
    body.style.backgroundPosition = 'center';
    body.style.backgroundRepeat = 'no-repeat';
    body.style.backgroundAttachment = 'fixed';
    body.classList.add('theme-background-active');

    // Apply to user_info background
    this.userInfoBg = imagePath;

    // Force sidebar to update with the same theme
    if (this.sidebarComponent) {
      this.sidebarComponent.userInfoBg = imagePath;
      this.sidebarComponent.refreshUserInfoBackground();
    }

    // Trigger change detection
    this.cdr.detectChanges();

    // Only log if the theme is different from the last logged theme
    if (this.lastLoggedTheme !== imagePath) {
      this.lastLoggedTheme = imagePath;
    }
  }

  private clearThemeConsistently() {
    // Clear body background
    const body = document.body;
    body.style.backgroundImage = '';
    body.classList.remove('theme-background-active');

    // Restore time-based background for user_info
    this.userInfoBg = this.getTimeBasedBackground();

    // Force sidebar to update with time-based background
    if (this.sidebarComponent) {
      this.sidebarComponent.forceRefreshTimeBasedBackground();
    }

    // Trigger change detection
    this.cdr.detectChanges();

    // Throttle console logging to prevent spam (only log once every 5 seconds)
    const now = Date.now();
    if (now - this.lastThemeClearLog > 5000 && this.lastLoggedBackground !== this.userInfoBg) {
      console.log('Theme cleared consistently, using time-based background:', this.userInfoBg);
      this.lastThemeClearLog = now;
      this.lastLoggedBackground = this.userInfoBg;
    }
  }

  // Theme Selection Modal Methods
  onThemeBtnClick() {
    this.themeSelectionModalOpen = true;
    // Close profile dropdown when opening theme modal
    this.profileDropdownOpen = false;
    this.cdr.detectChanges();
  }

  onCloseThemeSelectionModal() {
    this.themeSelectionModalOpen = false;
    this.cdr.detectChanges();
  }

  onThemeSelected(bannerPath: string) {
    this.onBackgroundImageSelected(bannerPath);
    this.onCloseThemeSelectionModal();
  }

  onThemePreview(bannerPath: string) {
    this.onBackgroundImagePreview(bannerPath);
  }

  onThemeToggled(isActive: boolean) {
    if (isActive) {
      // If turning on, apply saved theme or default theme via preview
      const savedTheme = localStorage.getItem('selectedBackgroundTheme');
      if (savedTheme) {
        // Preview saved theme
        this.onBackgroundImagePreview(savedTheme);
      } else {
        // Preview default theme (banner1.webp)
        const defaultTheme = 'assets/images/theme/banner1.webp';
        this.onBackgroundImagePreview(defaultTheme);
      }
    } else {
      // If turning off, emit clear signal to show time-based background via preview
      this.onBackgroundImagePreview('');

      // Force change detection to ensure UI updates immediately
      this.cdr.detectChanges();
    }

    // Note: Modal remains open, theme changes are applied as preview
    // Permanent changes only happen when user clicks Save
  }

  ngOnDestroy() {
    // Clean up interval
    if (this.backgroundUpdateInterval) {
      clearInterval(this.backgroundUpdateInterval);
    }

    // Clean up mutation timeout
    if (this.mutationTimeout) {
      clearTimeout(this.mutationTimeout);
    }

    // Clean up route change timeout
    if (this.routeChangeTimeout) {
      clearTimeout(this.routeChangeTimeout);
    }

    // Clean up mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    // Clean up storage event listener
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
    }
  }

  // Settings slider methods
  onSettingsClick(): void {
    this.showSettingsSlider = true;
    // Close profile dropdown when opening settings
    this.profileDropdownOpen = false;
  }

  onCloseSettingsSlider(): void {
    this.showSettingsSlider = false;
  }


 // Profile dropdown methods
 onProfileDropdownStateChange(state: { isOpen: boolean, userName: string | null, userImage: string | null }): void {
  this.profileDropdownOpen = state.isOpen;
  this.profileDropdownUserName = state.userName;
  this.profileDropdownUserImage = state.userImage;
  // Get userId from localStorage
  this.profileDropdownUserId = localStorage.getItem('userID');
  this.cdr.detectChanges();
}

// Navigate to profile view page
navigateToProfileView(event?: Event): void {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
  }
  if (this.profileDropdownUserId) {
    this.profileDropdownOpen = false;
    this.router.navigate(['/people/peopleview', this.profileDropdownUserId]);
  }
}

  onLogoutClick(): void {
    // Close the dropdown
    this.profileDropdownOpen = false;
    // Call header's logout method
    if (this.headerComponent) {
      this.headerComponent.logout();
    }
  }

  onSettingsSaved(settings: UserSettings): void {
    console.log('Settings saved:', settings);
    // Forward the settings to the header component
    if (this.headerComponent) {
      this.headerComponent.onSettingsSaved(settings);
      // Also refresh user image from localStorage as a fallback with a small delay
      setTimeout(() => {
        this.headerComponent.refreshUserImage();
      }, 100);
    }
    // Handle settings saved event if needed
  }

  // Mobile radial menu methods
  onMobileAddClick(): void {
    this.isMobileRadialMenuOpen = !this.isMobileRadialMenuOpen;
  }

  onMobileRadialMenuItemClick(item: any, event: Event): void {
    event.stopPropagation();
    this.isMobileRadialMenuOpen = false;

    if (item.queryParams) {
      this.router.navigate([item.route], { queryParams: item.queryParams });
    } else {
      this.router.navigate([item.route]);
    }
  }

  onMobileRadialMenuClose(event: Event): void {
    event.stopPropagation();
    this.isMobileRadialMenuOpen = false;
  }

  // Close mobile radial menu when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.isMobileRadialMenuOpen) {
      const target = event.target as HTMLElement;
      if (!target.closest('.mobile_quick_add') && !target.closest('.mobile_radial_menu')) {
        this.isMobileRadialMenuOpen = false;
      }
    }
    
    // Close popup dropdowns when clicking outside
    const target = event.target as HTMLElement;
    if (!target.closest('.popup-dropdown-container')) {
      // Close all open dropdowns
      this.popupDropdownOpen.set({});
    }
  }

  // Keyboard navigation support for mobile radial menu
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isMobileRadialMenuOpen) {
      switch (event.key) {
        case 'Escape':
          this.isMobileRadialMenuOpen = false;
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
          const index = parseInt(event.key) - 1;
          if (this.mobileRadialMenuItems[index]) {
            this.onMobileRadialMenuItemClick(this.mobileRadialMenuItems[index], event);
          }
          break;
      }
    }
  }

  // Notification popup methods
  markAsRead(notification: Notification, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.notificationService.markAsRead(notification.id);
    // Close the popup after marking as read
    this.notificationService.dismissPopup(notification.id);
  }

  dismissPopup(notificationId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.notificationService.dismissPopup(notificationId);
  }

  onPopupClick(notification: Notification, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    // Mark as read first
    this.notificationService.markAsRead(notification.id);
    
    // Get route based on notification object details
    const route = this.notificationService.getNotificationRoute(notification);
    
    if (route) {
      // Get fragment for scrolling to specific update/reply
      const fragment = this.notificationService.getNotificationFragment(notification);
      
      // Navigate with fragment if available
      if (fragment) {
        this.router.navigate(route, { fragment });
      } else {
        this.router.navigate(route);
      }
    }
    
    // Dismiss popup after navigation
    this.notificationService.dismissPopup(notification.id);
  }

  togglePin(notification: Notification) {
    this.notificationService.togglePin(notification.id);
  }

  onCommentClick(notification: Notification, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    // Mark as read first
    this.notificationService.markAsRead(notification.id);
    
    // Navigate to the corresponding task/project
    // You can customize this routing based on your notification structure
    if (notification.category === 'timesheet') {
      this.router.navigate(['/timesheets'], { 
        queryParams: { 
          taskId: notification.id,
          comment: 'true' 
        } 
      });
    } else {
      // Default to projects or general notifications
      this.router.navigate(['/projects/projects-all'], { 
        queryParams: { 
          taskId: notification.id,
          comment: 'true' 
        } 
      });
    }
    
    // Close the popup after navigation
    this.notificationService.dismissPopup(notification.id);
  }

  // Popup dropdown methods
  togglePopupDropdown(notificationId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    const currentState = this.popupDropdownOpen();
    const newState = { ...currentState };
    newState[notificationId] = !newState[notificationId];
    this.popupDropdownOpen.set(newState);
  }

  isPopupDropdownOpen(notificationId: string): boolean {
    return this.popupDropdownOpen()[notificationId] || false;
  }

  closePopupDropdown(notificationId: string) {
    const currentState = this.popupDropdownOpen();
    const newState = { ...currentState };
    newState[notificationId] = false;
    this.popupDropdownOpen.set(newState);
  }

  onPopupDropdownItemClick(notification: Notification, action: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    
    // Close dropdown first
    this.closePopupDropdown(notification.id);
    
    switch (action) {
      case 'mark-read':
        this.markAsRead(notification);
        break;
      case 'pin':
        this.togglePin(notification);
        break;
      case 'comment':
        this.onCommentClick(notification);
        break;
      case 'dismiss':
        this.dismissPopup(notification.id);
        break;
      case 'view-details':
        // Navigate to notification details or task details
        this.router.navigate(['/notifications'], { 
          queryParams: { 
            notificationId: notification.id 
          } 
        });
        this.dismissPopup(notification.id);
        break;
    }
  }


  // Handle image loading errors
  onImageError(notificationId: string): void {
    this.imageLoadError[notificationId] = true;
  }

  // Handle successful image loading
  onImageLoad(notificationId: string): void {
    this.imageLoadError[notificationId] = false;
  }

  // Get initials from name for avatar fallback
  getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  }
}
